import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Autocomplete,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Apps,
  Add,
  Edit,
  Visibility,
  Block,
  CheckCircle,
  Error,
  Refresh,
  Security,
  Business,
  Devices,
  Delete,
  VpnKey
} from '@mui/icons-material';
import adminApi from '../services/api';
import {
  VendorDeviceResponse,
  VendorDevicePermissionResponse,
  GrantVendorDevicePermissionRequest
} from '../types/api';

interface Application {
  id: string;
  package_name: string;
  signing_cert_sha256: string;
  is_active: boolean;
  environment: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  notes?: string;
  total_requests?: number;
  last_request?: string;
}

interface AppFormData {
  package_name: string;
  signing_cert_sha256: string;
  environment: 'testing' | 'production';
  notes?: string;
}

interface VendorPermissionFormData {
  vendor_device_id: string;
  expires_at?: string;
  granted_reason?: string;
}

const Applications: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [apps, setApps] = useState<Application[]>([]);

  const [vendorDevices, setVendorDevices] = useState<VendorDeviceResponse[]>([]);
  const [appPermissions, setAppPermissions] = useState<VendorDevicePermissionResponse[]>([]);
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [openPermissionDialog, setOpenPermissionDialog] = useState(false);
  const [editingApp, setEditingApp] = useState<Application | null>(null);
  
  // Form states
  const [formData, setFormData] = useState<AppFormData>({
    package_name: '',
    signing_cert_sha256: '',
    environment: 'testing',
    notes: ''
  });
  const [permissionFormData, setPermissionFormData] = useState<VendorPermissionFormData>({
    vendor_device_id: '',
    expires_at: '',
    granted_reason: ''
  });
  
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    loadApplications();
    loadVendors();
  }, []);

  useEffect(() => {
    if (selectedApp && activeTab === 1) {
      loadAppPermissions(selectedApp.id);
    }
  }, [selectedApp, activeTab]);

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApi.getApps();
      setApps(response.apps || []);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load applications';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadVendors = async () => {
    try {
      const response = await adminApi.getVendors({ active_only: true });
      
      // Load all vendor devices for permission assignment
      const allDevices: VendorDeviceResponse[] = [];
      for (const vendor of (response.vendors || [])) {
        try {
          const devicesResponse = await adminApi.getVendorDevices(vendor.vendor_id, { active_only: true });
          allDevices.push(...devicesResponse.devices);
        } catch (error) {
          console.warn(`Failed to load devices for vendor ${vendor.vendor_id}:`, error);
        }
      }
      setVendorDevices(allDevices);
    } catch (err: any) {
      console.error('Failed to load vendors:', err);
    }
  };

  const loadAppPermissions = async (appId: string) => {
    try {
      setPermissionsLoading(true);
      const response = await adminApi.getAppPermissions(appId, { active_only: false });
      setAppPermissions(response.permissions || []);
    } catch (err: any) {
      console.error('Failed to load app permissions:', err);
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handleCreateApp = async () => {
    try {
      setActionLoading('create');
      await adminApi.registerApp(formData);
      setOpenDialog(false);
      setFormData({ package_name: '', signing_cert_sha256: '', environment: 'testing', notes: '' });
      await loadApplications();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to create application';
      setError(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateApp = async () => {
    if (!editingApp) return;
    
    try {
      setActionLoading('update');
      await adminApi.updateApp(editingApp.id, {
        is_active: !editingApp.is_active,
        notes: formData.notes,
        reason: `Updated via admin dashboard by user`
      });
      setOpenDialog(false);
      setEditingApp(null);
      setFormData({ package_name: '', signing_cert_sha256: '', environment: 'testing', notes: '' });
      await loadApplications();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to update application';
      setError(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handleGrantPermission = async () => {
    if (!selectedApp) return;

    try {
      setActionLoading('grant-permission');
      const grantData: GrantVendorDevicePermissionRequest = {
        vendor_device_id: permissionFormData.vendor_device_id,
        expires_at: permissionFormData.expires_at || undefined,
        granted_reason: permissionFormData.granted_reason || undefined
      };
      
      await adminApi.grantVendorDevicePermission(selectedApp.id, grantData);
      setOpenPermissionDialog(false);
      setPermissionFormData({ vendor_device_id: '', expires_at: '', granted_reason: '' });
      await loadAppPermissions(selectedApp.id);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to grant permission';
      setError(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevokePermission = async (permissionId: string) => {
    if (!selectedApp) return;

    try {
      setActionLoading(`revoke-${permissionId}`);
      await adminApi.revokeVendorDevicePermission(selectedApp.id, permissionId);
      await loadAppPermissions(selectedApp.id);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to revoke permission';
      setError(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const openCreateDialog = () => {
    setEditingApp(null);
    setFormData({ package_name: '', signing_cert_sha256: '', environment: 'testing', notes: '' });
    setOpenDialog(true);
  };

  const openEditDialog = (app: Application) => {
    setEditingApp(app);
    setFormData({
      package_name: app.package_name,
      signing_cert_sha256: app.signing_cert_sha256,
      environment: app.environment as 'testing' | 'production',
      notes: app.notes || ''
    });
    setOpenDialog(true);
  };

  const openGrantPermissionDialog = (app: Application) => {
    setSelectedApp(app);
    setPermissionFormData({ vendor_device_id: '', expires_at: '', granted_reason: '' });
    setOpenPermissionDialog(true);
  };

  const getEnvironmentColor = (environment: string) => {
    switch (environment) {
      case 'production': return 'error';
      case 'staging': return 'warning';
      case 'testing': return 'info';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getVendorDeviceLabel = (device: VendorDeviceResponse) => {
    return `${device.vendor_name || 'Unknown'} - ${device.device_name} (ID: ${device.device_id})`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">
          📱 Application Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadApplications}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openCreateDialog}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            Register App
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Applications" icon={<Apps />} />
          <Tab label="Vendor Permissions" icon={<Security />} />
        </Tabs>
      </Card>

      {/* Tab Content */}
      {activeTab === 0 && (
        <>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>Total Apps</Typography>
                  <Typography variant="h3" fontWeight="bold">{apps.length}</Typography>
                </Box>
                <Apps sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>Active Apps</Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {apps.filter((app: Application) => app.is_active).length}
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>Production Apps</Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {apps.filter((app: Application) => app.environment === 'production').length}
                  </Typography>
                </Box>
                <Error sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>Total Requests</Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {apps.reduce((sum: number, app: Application) => sum + (app.total_requests || 0), 0).toLocaleString()}
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Applications Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Registered Applications
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Package Name</TableCell>
                  <TableCell>App ID</TableCell>
                  <TableCell>Environment</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last Used</TableCell>
                  <TableCell>Requests</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apps.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No applications registered yet. Click "Register App" to add your first application.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  apps.map((app: Application) => (
                    <TableRow key={app.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {app.package_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {app.id.substring(0, 8)}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={app.environment}
                          color={getEnvironmentColor(app.environment)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={app.is_active ? 'Active' : 'Inactive'}
                          color={app.is_active ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(app.created_at)}</TableCell>
                      <TableCell>{app.last_request ? formatDate(app.last_request) : 'Never'}</TableCell>
                      <TableCell>{(app.total_requests || 0).toLocaleString()}</TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="View Details">
                            <IconButton size="small">
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit App">
                            <IconButton size="small" onClick={() => openEditDialog(app)}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                              <Tooltip title="Manage Permissions">
                                <IconButton size="small" onClick={() => openGrantPermissionDialog(app)}>
                                  <VpnKey />
                                </IconButton>
                              </Tooltip>
                          <Tooltip title={app.is_active ? 'Deactivate' : 'Activate'}>
                            <IconButton size="small" onClick={() => openEditDialog(app)}>
                              <Block />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
        </>
      )}

      {/* Vendor Permissions Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Select Application
                </Typography>
                <List>
                  {apps.map((app) => (
                    <ListItem
                      key={app.id}
                      button
                      selected={selectedApp?.id === app.id}
                      onClick={() => setSelectedApp(app)}
                    >
                      <ListItemText
                        primary={app.package_name}
                        secondary={
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Chip label={app.environment} size="small" />
                            <Chip 
                              label={app.is_active ? 'Active' : 'Inactive'} 
                              color={app.is_active ? 'success' : 'error'}
                              size="small" 
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            {selectedApp ? (
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Vendor Device Permissions for {selectedApp.package_name}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => openGrantPermissionDialog(selectedApp)}
                      size="small"
                    >
                      Grant Permission
                    </Button>
                  </Box>

                  {permissionsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Vendor</TableCell>
                            <TableCell>Device</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Granted</TableCell>
                            <TableCell>Expires</TableCell>
                            <TableCell>Usage</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {appPermissions.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} sx={{ textAlign: 'center', py: 3 }}>
                                <Typography variant="body2" color="text.secondary">
                                  No vendor permissions granted yet.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            appPermissions.map((permission) => (
                              <TableRow key={permission.id}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Business fontSize="small" />
                                    {permission.vendor_code}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Devices fontSize="small" />
                                    {permission.device_name}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={permission.is_active ? 'Active' : 'Inactive'}
                                    color={permission.is_active ? 'success' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>{formatDateTime(permission.granted_at)}</TableCell>
                                <TableCell>
                                  {permission.expires_at ? formatDateTime(permission.expires_at) : 'Never'}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {permission.total_requests} requests
                                  </Typography>
                                  {permission.last_used_at && (
                                    <Typography variant="caption" color="text.secondary">
                                      Last: {formatDate(permission.last_used_at)}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Tooltip title="Revoke Permission">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRevokePermission(permission.id)}
                                      disabled={actionLoading === `revoke-${permission.id}`}
                                    >
                                      {actionLoading === `revoke-${permission.id}` ? (
                                        <CircularProgress size={16} />
                                      ) : (
                                        <Delete />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="h6" color="text.secondary">
                    Select an application to view its vendor permissions
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      )}

      {/* Create/Edit App Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingApp ? 'Edit Application' : 'Register New Application'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Package Name"
              value={formData.package_name}
              onChange={(e) => setFormData({ ...formData, package_name: e.target.value })}
              sx={{ mb: 3 }}
              required
              disabled={!!editingApp}
              helperText={editingApp ? "Package name cannot be changed" : "Android package name (e.g., com.example.app)"}
            />
            <TextField
              fullWidth
              label="Signing Certificate SHA256"
              value={formData.signing_cert_sha256}
              onChange={(e) => setFormData({ ...formData, signing_cert_sha256: e.target.value })}
              sx={{ mb: 3 }}
              required
              disabled={!!editingApp}
              helperText={editingApp ? "Certificate hash cannot be changed" : "SHA256 hash of the app signing certificate"}
            />
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Environment</InputLabel>
              <Select
                value={formData.environment}
                label="Environment"
                onChange={(e) => setFormData({ ...formData, environment: e.target.value as 'testing' | 'production' })}
                disabled={!!editingApp}
              >
                <MenuItem value="testing">Testing</MenuItem>
                <MenuItem value="production">Production</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Notes (Optional)"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={3}
              helperText="Additional notes about this application"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={editingApp ? handleUpdateApp : handleCreateApp}
            variant="contained"
            disabled={!formData.package_name || !formData.signing_cert_sha256 || actionLoading !== null}
          >
            {actionLoading ? <CircularProgress size={20} /> : (editingApp ? 'Update' : 'Register')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Grant Permission Dialog */}
      <Dialog open={openPermissionDialog} onClose={() => setOpenPermissionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Grant Vendor Device Permission
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Grant permission for <strong>{selectedApp?.package_name}</strong> to use a vendor device combination.
            </Typography>
            
            <Autocomplete
              fullWidth
              options={vendorDevices}
              getOptionLabel={getVendorDeviceLabel}
              value={vendorDevices.find(d => d.id === permissionFormData.vendor_device_id) || null}
              onChange={(_, value) => setPermissionFormData({
                ...permissionFormData,
                vendor_device_id: value?.id || ''
              })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vendor Device"
                  required
                  helperText="Select the vendor/device combination to grant permission for"
                />
              )}
              sx={{ mb: 3 }}
            />
            
            <TextField
              fullWidth
              label="Expiration Date (Optional)"
              type="datetime-local"
              value={permissionFormData.expires_at}
              onChange={(e) => setPermissionFormData({
                ...permissionFormData,
                expires_at: e.target.value
              })}
              sx={{ mb: 3 }}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty for no expiration"
            />
            
            <TextField
              fullWidth
              label="Reason (Optional)"
              value={permissionFormData.granted_reason}
              onChange={(e) => setPermissionFormData({
                ...permissionFormData,
                granted_reason: e.target.value
              })}
              multiline
              rows={3}
              helperText="Reason for granting this permission"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPermissionDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleGrantPermission}
            variant="contained"
            disabled={!permissionFormData.vendor_device_id || actionLoading === 'grant-permission'}
          >
            {actionLoading === 'grant-permission' ? <CircularProgress size={20} /> : 'Grant Permission'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Applications; 